import { useLazyQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import MessageTemplateFilters from "@components/forms/MessageTemplateFilters";
import MessageTemplateForm from "@components/forms/MessageTemplateForm";
import HandleQuery from "@components/HandleQuery";
import Tag from "@components/labels/Tag";
import styles from "@components/MessageTemplatesTable.module.css";
import Modal, { ModalElement } from "@components/Modal";
import Table, { TableHeader } from "@components/Table";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading3 from "@components/typography/Heading3";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import {
  GetMessageTemplateCategoriesDocument,
  GetMessageTemplateCategoriesQuery,
  GetMessageTemplateCategoriesQueryVariables,
  GetMessageTemplatesDocument,
  GetMessageTemplatesQuery,
  GetMessageTemplatesQueryVariables,
  MessageTemplateDestroyDocument,
  MessageTemplateDestroyMutation,
  MessageTemplateDestroyMutationVariables,
  MessageTemplatesSortByEnum,
  SortDirectionEnum
} from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";

type Filters = ComponentProps<typeof MessageTemplateFilters>["filters"];

type Template = NonNullable<
  NonNullable<NonNullable<GetMessageTemplatesQuery["messageTemplates"]["edges"]>[number]>["node"]
>;

const MessageTemplatesTable = () => {
  const intl = useIntl();
  const destroyOpener = useOpener();
  const myExpert = useMyExpert();
  const templateOpener = useOpener();
  const viewOpener = useOpener();

  const templateModalRef = useRef<ModalElement>(null);

  const handleTemplateError = useCallback(() => {
    if (templateModalRef.current) {
      templateModalRef.current.scrollToTop();
    }
  }, [templateModalRef]);

  const [getMessageTemplates, getMessageTemplatesQuery] = useLazyQuery<
    GetMessageTemplatesQuery,
    GetMessageTemplatesQueryVariables
  >(GetMessageTemplatesDocument);

  const [getCategories, getCategoriesQuery] = useLazyQuery<
    GetMessageTemplateCategoriesQuery,
    GetMessageTemplateCategoriesQueryVariables
  >(GetMessageTemplateCategoriesDocument);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  useEffect(() => {
    if (!getCategoriesQuery.loading && getCategoriesQuery.data?.messageTemplateCategories.pageInfo.hasNextPage) {
      getCategoriesQuery.fetchMore({
        variables: { cursor: getCategoriesQuery.data?.messageTemplateCategories.pageInfo.endCursor ?? "" }
      });
    }
  }, [getCategoriesQuery]);

  const allCategories = useNodesFromConnection(getCategoriesQuery.data?.messageTemplateCategories);

  const categoryOptions = allCategories.map(category => ({
    label: category.title,
    value: category.id
  }));

  const [filters, setFilters] = useState<Filters>({
    search: ""
  });

  const [sortBy, setSortBy] = useState<MessageTemplatesSortByEnum>(MessageTemplatesSortByEnum.TITLE);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.DESCENDING);
  const [modifyingTemplate, setModifyingTemplate] = useState<Template | null>(null);

  const [destroyMessageTemplate, client] = useMutationWithValidation<
    MessageTemplateDestroyMutation,
    MessageTemplateDestroyMutationVariables
  >(MessageTemplateDestroyDocument, "messageTemplate", {
    update: () => {
      // This kicks out all of the message templates queries so we will re-make them. If you're flipping between filters
      // deleting will update the message templates, but switching filters will have the cached value
      client.cache.evict({ fieldName: "messageTemplates", id: "ROOT_QUERY" });
      getMessageTemplatesQuery.refetch();
    }
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const sanitizedContent = useSanitizeHtml(modifyingTemplate?.content, null, modifyingTemplate);

  const handleTemplateEdit = (messageTemplate: Template) => () => {
    viewOpener.close();
    setModifyingTemplate(messageTemplate);
    templateOpener.open();
  };

  const handleTemplateCreate = () => {
    setModifyingTemplate(null);
    templateOpener.open();
  };

  const handleTemplateDestroy = (messageTemplate: Template) => () => {
    setModifyingTemplate(messageTemplate);
    destroyOpener.open();
  };

  const handleTemplateDestroyed = useCallback(() => {
    if (modifyingTemplate) {
      destroyMessageTemplate({
        id: modifyingTemplate.id
      });
    }
  }, [destroyMessageTemplate, modifyingTemplate]);

  useEffect(() => {
    getMessageTemplates({
      variables: {
        categoryId: filters.categoryId,
        search: filters.search,
        sortBy,
        sortDirection
      }
    });
  }, [filters, getMessageTemplates, sortBy, sortDirection]);

  const messageTemplates = useNodesFromConnection(getMessageTemplatesQuery.data?.messageTemplates);

  const tableHeaders: TableHeader<MessageTemplatesSortByEnum>[] = [
    { columnSortBy: MessageTemplatesSortByEnum.TITLE },
    { columnSortBy: MessageTemplatesSortByEnum.CATEGORY },
    { columnSortBy: MessageTemplatesSortByEnum.UPDATED_AT },
    { messageKey: "pages.expert.messageTemplates.tableHeadings.actions" }
  ];

  const handleViewTemplate = (template: Template) => {
    setModifyingTemplate(template);
    viewOpener.open();
  };

  return (
    <>
      <div>
        <IonGrid>
          <IonRow className="ion-align-items-center">
            <IonCol className={styles.filters}>
              <MessageTemplateFilters
                categoryOptions={categoryOptions}
                filters={filters}
                onChange={handleFilterChange}
              />
            </IonCol>
            <IonCol className="align-right" size="auto">
              <Button fill="clear" routerLink={`/${myExpert ? "expert" : "admin"}/message-template-categories`}>
                <IconBase icon="edit" slot="start" />
                <FormattedMessage id="pages.expert.messageTemplates.editCategories" />
              </Button>
              <Button onClick={handleTemplateCreate}>
                <FormattedMessage id="pages.expert.messageTemplates.new" />
              </Button>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      <HandleQuery query={getMessageTemplatesQuery}>
        {data => (
          <>
            <Table<MessageTemplatesSortByEnum>
              fetchMore={getMessageTemplatesQuery.fetchMore}
              headerKey="pages.expert.messageTemplates.tableHeadings"
              headers={tableHeaders}
              pageInfo={data?.messageTemplates.pageInfo}
              setSortBy={setSortBy}
              setSortDirection={setSortDirection}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              {messageTemplates.map(template => (
                <tr key={template.id}>
                  <td>
                    <ButtonLink onClick={() => handleViewTemplate(template)}>{template.title}</ButtonLink>
                  </td>
                  <td>
                    <Tag color="secondary-500" text={template.messageTemplateCategory.title} />
                  </td>
                  <td>
                    <DateStamp showWeekday={false} value={template.updatedAt} />
                  </td>
                  <td>
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.edit"
                      fill="clear"
                      icon="edit"
                      onClick={handleTemplateEdit(template)}
                    />
                    <br />
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      fill="clear"
                      icon="trash"
                      onClick={handleTemplateDestroy(template)}
                    />
                  </td>
                </tr>
              ))}
            </Table>
            {messageTemplates.length === 0 && (
              <div className={styles.empty}>
                <img
                  alt={intl.formatMessage({ id: "accessibility.altTags.ladyWritingMessage" })}
                  className={styles.emptyImage}
                  src={imageUrl("ladyWritingMessage")}
                />
                <div className={styles.bottomSpacing}>
                  <BodyCopy>
                    <FormattedMessage id="pages.expert.messageTemplates.empty" />
                  </BodyCopy>
                </div>
              </div>
            )}

            <DeleteConfirmationAlert
              messageKey="pages.expert.messageTemplates.delete"
              onDelete={handleTemplateDestroyed}
              opener={destroyOpener}
            />
          </>
        )}
      </HandleQuery>
      <Modal
        opener={templateOpener}
        title={
          modifyingTemplate
            ? intl.formatMessage({ id: "pages.expert.messageTemplates.editTemplate" })
            : intl.formatMessage({ id: "pages.expert.messageTemplates.addTemplate" })
        }
      >
        {() => (
          <MessageTemplateForm
            autofocus
            categoryOptions={categoryOptions}
            messageTemplate={modifyingTemplate}
            onCancel={templateOpener.close}
            onError={handleTemplateError}
            onSuccess={templateOpener.close}
          />
        )}
      </Modal>
      <Modal opener={viewOpener} title={intl.formatMessage({ id: "pages.expert.messageTemplates.preview" })}>
        {() => (
          <div>
            {modifyingTemplate && (
              <>
                <Heading3>{modifyingTemplate.title}</Heading3>
                <div className="ion-margin-top">{sanitizedContent}</div>
                <div className="align-right">
                  <Button fill="outline" onClick={viewOpener.close}>
                    <FormattedMessage id="dictionary.close" />
                  </Button>
                  <Button onClick={handleTemplateEdit(modifyingTemplate)}>
                    <FormattedMessage id="dictionary.edit" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default MessageTemplatesTable;
